import { ReactNode } from 'react';
import { Region } from 'utils/constants/location';
import { CityString } from 'utils/constants/location_legacy';

export const renderIfElse = (
  condition: boolean,
  ifTrue: ReactNode = null,
  ifFalse: ReactNode = null,
) => (condition ? ifTrue : ifFalse);

/**
 * Checks if the item exists. This is useful to replace `[].filter(v => !!v)` as the result of
 * this array will be typed without the potential `null` or `undefined` value, which can cause
 * headaches needing to account for them later in either the chain or implementing code.
 *
 * See: https://stackoverflow.com/a/46700791
 *
 * @param value Value to type check
 * @returns If value exists and correct type predicate
 */
export function exists<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

export type CronObj = Partial<
  Record<'min' | 'hr' | 'monthDay' | 'month' | 'weekDay', number>
>;

function parseCronValue(val: string) {
  const value = Number.parseInt(val, 10);

  return Number.isNaN(value) ? undefined : value;
}

export function parseCron(cron = ''): CronObj {
  const [min, hr, monthDay, month, weekDay] = cron.split(' ');

  return {
    min: parseCronValue(min),
    hr: parseCronValue(hr),
    monthDay: parseCronValue(monthDay),
    month: parseCronValue(month),
    weekDay: parseCronValue(weekDay),
  };
}

function toCronValue(val?: number) {
  return (val ?? '*').toString();
}

export function toCron({ min, hr, monthDay, month, weekDay }: CronObj): string {
  return [
    toCronValue(min),
    toCronValue(hr),
    toCronValue(monthDay),
    toCronValue(month),
    toCronValue(weekDay),
  ].join(' ');
}

const isObject = (object: Object | any) => {
  return object != null && typeof object === 'object';
};
/* eslint-disable no-restricted-syntax */
export const isDeepEqualGroup = (object1: any, object2: any) => {
  const objKeys1 = Object.keys(object1);
  const objKeys2 = Object.keys(object2);

  if (objKeys1.length !== objKeys2.length) return false;

  for (const key of objKeys1) {
    const value1 = object1[key];
    const value2 = object2[key];

    const isObjects = isObject(value1) && isObject(value2);

    if (
      (isObjects && !isDeepEqualGroup(value1, value2)) ||
      (!isObjects && value1 !== value2)
    ) {
      return false;
    }
  }

  return true;
};
/* eslint-enable no-restricted-syntax */

export const cityMapCanada = {
  CALGARY: 5,
  TORONTO: 6,
  VANCOUVER: 7,
  EDMONTON: 8,
};

export const cityMapUnitedStates = {
  AUSTIN: 9,
  LOS_ANGELES: 10,
  SAN_ANTONIO: 14,
  SAN_FRANCISCO: 11,
  HOUSTON: 12,
  DALLAS: 15,
  FORT_WORTH: 16,
  // ROUND_ROCK: 17,
  PFLUGERVILLE: 18,
  JACKSONVILLE: 1858,
  MIAMI: 1859,
  ORLANDO: 1860,
  TAMPA: 1863,
  INDIANAPOLIS: 2451,
  CORPUS_CHRISTI: 362,
};

type CityMap = {
  [key in CityString]: number;
};

export const cityMap: CityMap = { ...cityMapCanada, ...cityMapUnitedStates };

type CityMapId = {
  [key: number]: CityString;
};

export const cityMapId: CityMapId = {
  5: 'CALGARY',
  6: 'TORONTO',
  7: 'VANCOUVER',
  8: 'EDMONTON',
  9: 'AUSTIN',
  10: 'LOS_ANGELES',
  11: 'SAN_FRANCISCO',
  12: 'HOUSTON',
  14: 'SAN_ANTONIO',
  15: 'DALLAS',
  16: 'FORT_WORTH',
  // 17: 'ROUND_ROCK',
  18: 'PFLUGERVILLE',
  1858: 'JACKSONVILLE',
  1859: 'MIAMI',
  1860: 'ORLANDO',
  1863: 'TAMPA',
};

export const titleCase = (str: string): string => {
  if (!str) {
    return '';
  }

  return str
    .split(/([ -])/g) // Split the input string by spaces and hyphens
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Convert each word to lower case and capitalize the first letter
    .join(''); // Join the words back together
};

export const isCityMatch = (
  projectCity: string | undefined,
  targetCity: CityString | CityString[],
): boolean => {
  if (!projectCity) return false;

  // Handle array of cities
  if (Array.isArray(targetCity)) {
    return targetCity.some((city) => projectCity.toUpperCase() === city);
  }

  // Handle single city (existing behavior)
  return projectCity.toUpperCase() === targetCity;
};

export const isRegionMatch = (
  projectRegion: string | null,
  targetRegion: Region | Region[],
): boolean => {
  if (!projectRegion) return false;

  // Handle array of cities
  if (Array.isArray(targetRegion)) {
    return targetRegion.some(
      (region) => projectRegion.toUpperCase() === region.toUpperCase(),
    );
  }

  // Handle single city (existing behavior)
  return projectRegion.toUpperCase() === targetRegion.toUpperCase();
};

export const calcTotalPages = (itemCount: number, itemsPerPage: number = 20) => {
  return itemCount ? Math.ceil(itemCount / itemsPerPage) : 1;
};

export const toSentenceCase = (str: string) => {
  if (!str) return str;

  // First, convert the entire string to lowercase
  const lowerCaseStr = str.toLowerCase();

  // Then, capitalize the first character and concatenate it with the rest of the string
  return lowerCaseStr.charAt(0).toUpperCase() + lowerCaseStr.slice(1);
};

export const normalizeCityName = (cityName: string): CityString | undefined => {
  const normalized = cityName.toUpperCase().replace(/\s+/g, '_');

  return normalized in cityMap ? (normalized as CityString) : undefined;
};

export const isEmptyObject = (obj: Object) => {
  return Object.keys(obj).length === 0 && obj.constructor === Object;
};
